<!--
 * @Author: zhubincong
 * @Date: 2020-2-24 16:06:43
 * @Desc: 风险监测首页
 -->
<template>
  <div class="risk-monitor-container flex-1">
    <div class="risk-monitor-content flex-1">
      <!-- 左侧看板 -->
      <div class="risk-monitor__left">
        <component
          :is="panel"
          v-for="panel in leftPanel"
          :key="panel"
          :ref="initialLowercase(panel)"
          :geo-data="geoData"
          :active="activePanel === initialLowercase(panel)"
          :size-mode="sizeMode"
          @click.native="handlePanelClick(initialLowercase(panel))"
        />
      </div>

      <!-- 中间看板 -->
      <div class="risk-monitor__center">
        <!-- 中心地图 -->
        <CenterMap
          ref="centerMapRef"
          :title="mapTitle"
          :size-mode="sizeMode"
          :geo-data="geoData"
          @mounted="handleMapMounted"
        />
        <!-- 中间底部看板 -->
        <div class="risk-monitor__center-bottom">
          <div class="risk-monitor__center-bottom-empty-block" />
          <component
            :is="panel"
            v-for="panel in middlePanel"
            :key="panel"
            :ref="initialLowercase(panel)"
            :geo-data="geoData"
            :size-mode="sizeMode"
            :active="activePanel === initialLowercase(panel)"
            @click.native="handlePanelClick(initialLowercase(panel))"
          />
          <div class="risk-monitor__center-bottom-empty-block" />
        </div>
      </div>

      <!-- 右侧看板 -->
      <div class="risk-monitor__right">
        <component
          :is="panel"
          v-for="panel in rightPanel"
          :key="panel"
          :ref="initialLowercase(panel)"
          :geo-data="geoData"
          :active="activePanel === initialLowercase(panel)"
          @click.native="handlePanelClick(initialLowercase(panel))"
        />
      </div>
    </div>
  </div>
</template>

<script>
import WarnStatus from './Panel/WarnStatus.vue' // 预警整体状况统计
import DutyInfo from './Panel/DutyInfo.vue' // 当前值班信息
import WarnNumberRanking from './Panel/WarnNumberRanking.vue' // 运营专员预警审核数量排名

import CenterMap from './Panel/CenterMap.vue' // 中心地图
import RegionWarnDistribution from './Panel/RegionWarnDistribution.vue' // 区域预警分布统计
import WarnDistributionStatistics from './Panel/WarnDistributionStatistics.vue' // 预警数量分布统计

import CompanyAccessRate from './Panel/CompanyAccessRate' // 企业监管率
import IndustryDistribution from './Panel/IndustryDistribution.vue' // 企业行业分布
import ExpiredWarnRanking from './Panel/ExpiredWarnRanking.vue' // 企业超期未处置预警排名

import http from '@/api/common.api.js'
import geoData from '@/assets/GeoJSON/jiaxing.json'
import { PANEL_TITLE } from './constant.js'

import { dashPath, dashLine, slicePath } from './g2CustomShap'
dashPath()
dashLine()
slicePath()

export default {
  name: 'RiskMonitoring',
  components: {
    WarnStatus,
    DutyInfo,
    WarnNumberRanking,
    CenterMap,
    RegionWarnDistribution,
    WarnDistributionStatistics,
    CompanyAccessRate,
    IndustryDistribution,
    ExpiredWarnRanking
  },
  provide() {
    return {
      $riskMonitor: this
    }
  },
  props: {
    // 屏幕尺寸类型
    sizeMode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      leftPanel: ['WarnStatus', 'DutyInfo', 'WarnNumberRanking'], // 左侧看板
      middlePanel: ['RegionWarnDistribution', 'WarnDistributionStatistics'], // 中间看板
      rightPanel: ['CompanyAccessRate', 'IndustryDistribution', 'ExpiredWarnRanking'], // 右侧看板
      activePanel: 'warnStatus',
      geoData: null
    }
  },
  computed: {
    mapTitle() {
      return PANEL_TITLE[this.activePanel]
    }
  },
  created() {
    this.getMapGeoData()
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  methods: {
    /**
     * @author chenguanbin
     * @date 2020-04-16 10:12:45
     * @desc 首字母小写
     */
    initialLowercase(str) {
      return `${str[0].toLocaleLowerCase()}${str.slice(1)}`
    },

    /**
     * @author chenguanbin
     * @date 2020-04-15 16:30:33
     * @desc 获取地图 Geo 数据
     */
    async getMapGeoData() {
      try {
        const { data } = await http.queryMapJson()
        this.geoData = JSON.parse(data)
      } catch (error) {
        console.error(error)
        // 请求报错则使用默认数据
        this.geoData = geoData
      }
    },

    /**
     * @author zhubincong
     * @date 2020-03-10 17:08:29
     * @desc 地图挂载完触发
     */
    handleMapMounted(mapChart, mapView, mapDataView) {
      // 这里的思路是获取mapChart后传给其他业务子组件，相关业务代码都交由业务组件自己处理
      // 不希望被响应式处理
      this.mapChart = mapChart
      this.mapView = mapView
      this.mapDataView = mapDataView
      this.$refs[this.activePanel][0].renderMap()
      // 每5分钟刷新数据
      this.interval = setInterval(this.refreshData, 5 * 60 * 1e3)
    },

    /**
     * @author zhubincong
     * @date 2020-03-18 17:02:46
     * @desc 刷新数据
     */
    refreshData() {
      Object.values(this.$refs).forEach(panel => {
        panel[0].refreshData()
      })
    },

    /**
     * @author zhubincong
     * @date 2020-03-11 18:41:47
     * @desc 点击面板切换地图数据
     */
    handlePanelClick(panel) {
      if (this.activePanel === panel) return
      this.$refs.centerMapRef.showRangeRateLegend = false
      this.activePanel = panel
      this.$refs[panel][0].renderMap()
    }
  }
}
</script>

<style lang="scss" scoped>
.risk-monitor-container {
  color: #caf7ff;
  background-image: url('~@/assets/images/RiskMonitoring/map-bg.png');
  padding: 16px 16px;
  flex-shrink: 0;
  display: flex;
}
.risk-monitor-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  position: relative;
}
.risk-monitor__left,
.risk-monitor__center,
.risk-monitor__right {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  min-height: 0;
}
.risk-monitor__center-bottom {
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 9;
  display: flex;
  width: 100%;
  justify-content: space-between;
}
.risk-monitor__left,
.risk-monitor__right {
  z-index: 99;
}
</style>

<style lang="scss" scoped>
@media screen and (min-width: 1900px) {
  .risk-monitor__center-bottom-empty-block {
    width: 460px;
    height: 376px;
  }
}

@media screen and (min-width: 1600px) and (max-width: 1899px) {
  .risk-monitor__center-bottom-empty-block {
    width: 383px;
    height: 313px;
  }
}

@media screen and (max-width: 1599px) {
  .risk-monitor__center-bottom-empty-block {
    width: 306px;
    height: 250px;
  }
}
</style>
